import { configSite } from "@pub/config";

import generateJsonLd from "./jsonLd";
import { MetaDataProps } from "./types";

type MetaDescriptor =
  | { title: string }
  | { name: string; content: string }
  | { property: string; content: string }
  | { tagName: "link"; rel: string; href: string }
  | { "script:ld+json": Record<string, unknown> };

function MetaData(props: MetaDataProps): MetaDescriptor[] {
  const {
    type,
    title,
    description,
    image,
    path,
    ogFields,
    robots = "index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1",
    canonicalUrl: canonicalUrlProp,
    sponsored,
  } = props;
  const url = `${configSite.address}${path}`;

  const canonicalUrl = canonicalUrlProp
    ? canonicalUrlProp.match(/^https?:\/\//)
      ? canonicalUrlProp
      : `${configSite.address}${
          canonicalUrlProp.startsWith("/")
            ? canonicalUrlProp
            : `/${canonicalUrlProp}`
        }`
    : url;

  const ogImage = ogFields?.image || image;

  const meta: MetaDescriptor[] = [
    ...(configSite.meta ?? []),
    { title: `${title} | ${configSite.name}` },
    { name: "description", content: description || "" },
    { name: "robots", content: robots },
    { tagName: "link", rel: "canonical", href: canonicalUrl },
    { property: "og:locale", content: "en_SG" },
    {
      property: "og:type",
      content: type === "Article" ? "article" : "website",
    },
    { property: "og:title", content: ogFields?.title || title },
    {
      property: "og:description",
      content: ogFields?.description || description || "",
    },
    { property: "og:url", content: url },
    { property: "og:site_name", content: configSite.name },
    { name: "twitter:card", content: "summary_large_image" },
    { name: "format-detection", content: "telephone=no" },
  ];

  if (sponsored) {
    meta.push({ name: "sponsored", content: "true" });
  }

  if (ogImage) {
    meta.push({ property: "og:image", content: ogImage.src });
    if (ogImage.srcWidth) {
      meta.push({
        property: "og:image:width",
        content: String(ogImage.srcWidth),
      });
    }
    if (ogImage.srcHeight) {
      meta.push({
        property: "og:image:height",
        content: String(ogImage.srcHeight),
      });
    }
  }

  if (configSite.twitterSite) {
    meta.push({ name: "twitter:site", content: configSite.twitterSite });
  }

  meta.push({
    "script:ld+json": generateJsonLd({
      path,
      metaData: props,
      canonicalUrl,
    }),
  });

  return meta;
}

export default MetaData;
